import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import {
  McButton,
} from "@maersk-global/mds-react-wrapper/components-core";
import DetectionAndResponseSection from "./DetectionAndResponseSection";
import SectionContent from "./SectionContent";
import RootCauseIdentificationSection from "./RootCauseIdentificationSection";
import RootCausection from "./RootCausection";
import JiraSection from "./JiraSection";
import PTaskSection from "./PTaskSection";
import ReportTrackingMethodManager from "./ReportTrackingMethodManager";
import RecurrenceAnalysisPanel from "./RecurrenceAnalysisPanel";
import ApprovalWorkflowTracker from "../ApprovalWorkflow/ApprovalWorkflowTracker";
import ApprovalPanel from "../ApprovalWorkflow/ApprovalPanel";
import ApprovalHistory from "../ApprovalWorkflow/ApprovalHistory";
import "../ApprovalWorkflow/ApprovalWorkflow.css";
import "./style.css";
import "../../Pages/EditPostmortems/editPostmortem.css";
import { convertEpochToDateFormat } from "../../Utils/convertEpochToDateFormat";
import { formatAssignedToDisplay } from "../../Utils/formatAssignedToDisplay";
import { getPastReferencesList, joinReferenceIds } from "../../Utils/recurrenceReferences";
import RecommendedActionsModal from "./RecommendedActionsModal";
import RcaQualityPanel, { useRcaCache, AiSuggestionsInline } from "./RcaQualityPanel";
import { fetchMttrInsights } from "../../services/mttrInsightsService";
import { fetchIncidentMetadata } from "../../services/metadataService";
import { useQueryClient } from "react-query";
import { cachedRequest } from "../../Utils/cachedRequest";

const PRIORITY_APPEARANCE = {
  "0": "error",
  "1": "warning",
  "2": "info",
};

const STATUS_APPEARANCE = {
  DONE: "success",
  DRAFT: "warning",
  LEADERSHIP_REVIEW: "info",
  INCOMPLETE: "error",
};

const getPriorityLabel = (dbValue) => {
  if (dbValue === undefined || dbValue === null || dbValue === "") return "";
  const raw = String(dbValue).trim();
  const map = { "0": "P0", "1": "P1", "2": "P2", "3": "P3", "4": "P4" };
  if (map[raw]) return map[raw];
  if (/^P\d+$/i.test(raw)) return raw.toUpperCase();
  return `P${raw}`;
};

function SectionCard({ id, title, children, action, insight, sectionName, dim, showAiBtn }) {
  const [aiOpen, setAiOpen] = useState(false);
  const hasInsight = !!insight && (
    insight.recommendations?.length > 0 ||
    insight.missing?.length > 0 ||
    !!insight.whyLow
  );
  const showPanel = aiOpen && hasInsight;

  return (
    <div id={id} className="sre-section-card">
      <div className="sre-section-header" style={{ background: "#42B0D5" }}>
        <span className="sre-section-header-title">{title}</span>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {(showAiBtn || hasInsight) && (
            <McButton
              appearance="inverse"
              variant="outlined"
              fit="small"
              icon="sparkle"
              label="AI Review"
              onClick={() => setAiOpen((v) => !v)}
            />
          )}
          {action && action}
        </div>
      </div>
      <div className={`sre-section-body${showPanel ? " sre-section-body--with-ai" : ""}`}>
        <div className={showPanel ? "sre-section-content" : undefined}>
          {children}
        </div>
        {showPanel && (
          <div className="sre-section-ai-col">
            <AiSuggestionsInline insight={insight} sectionName={sectionName || title} dim={dim} onClose={() => setAiOpen(false)} />
          </div>
        )}
      </div>
    </div>
  );
}

function Report({
  postmortemData,
  uploadedSummaryImages,
  uploadedPrecedingEventsImages,
  uploadedImpactImages,
  uploadedDetectionAndResponseImages,
  uploadedRootCauseImages,
  rootCauseIdentificationUploadedImages,
  jiraUploadedImages,
  ptaskUploadedImages,
  fetchRcaQuality,
  postMortemId,
  incidentID,
  fetchEnhancements,
  onRecurrenceChanged,
  onShare,
  onExportPdf,
  onEdit,
  canUserEditReport,
  userIsAdmin,
  onSavePreventiveMeasures,
  pdfDownloading,
  approvalStatus,
  approvalHistory,
  approvalLoading,
  canUserApprove,
  isUserAuthorizedToViewApproval,
  handleApproveLeadershipReview,
  handleRejectLeadershipReview,
}) {
  const { instance, accounts } = useMsal();
  const accountId = accounts[0]?.homeAccountId;
  const queryClient = useQueryClient();
  const [recommendationModal, seRecommendationModal] = useState(false);
  const [summaryAiOpen, setSummaryAiOpen] = useState(false);

  const handleRecommedationModalClose = () => seRecommendationModal(false);
  const handleRecommedationModalOpen = () => seRecommendationModal(true);

  const { data: rcaData } = useRcaCache(postMortemId);
  const findInsight = (keywords) =>
    rcaData?.detailedInsights?.find((ins) =>
      keywords.some((k) => ins.key?.toLowerCase().includes(k))
    );
  const findDim = (keywords) =>
    rcaData?.dims?.find((d) =>
      keywords.some((k) => d.label?.toLowerCase().includes(k))
    );

  const [mttrInsights, setMttrInsights] = useState(null);
  useEffect(() => {
    if (!incidentID || !accountId) return;
    cachedRequest(queryClient, ["mttrInsights", incidentID], () => fetchMttrInsights(incidentID, instance, accounts))
      .then((data) => { if (data) setMttrInsights(data); })
      .catch(() => { });
  }, [incidentID, instance, accountId]);

  const [incidentMetadata, setIncidentMetadata] = useState(null);
  useEffect(() => {
    if (!incidentID || !accountId) return;
    cachedRequest(queryClient, ["incidentMetadata", incidentID], () => fetchIncidentMetadata(incidentID, instance, accounts))
      .then((data) => { if (data) setIncidentMetadata(data); })
      .catch(() => { });
  }, [incidentID, instance, accountId]);


  // API returns *_seconds; convert to minutes. Fall back to postmortem document fields.
  const mttr = mttrInsights?.ttr_seconds != null
    ? parseFloat(mttrInsights.ttr_seconds) / 60
    : postmortemData?.detectionResponse?.mttrecover;
  const mttd = mttrInsights?.ttd_seconds != null
    ? parseFloat(mttrInsights.ttd_seconds) / 60
    : postmortemData?.detectionResponse?.mttd;
  const capturedByAlert = postmortemData.channelName?.toLowerCase().includes("alert");

  const formatUtcDate = (raw) => {
    if (!raw) return "NA";
    // Normalise: replace space separator with T, append Z if no timezone indicator
    const normalised = raw.replace(" ", "T").replace(/(\.\d+)?$/, (m) =>
      /Z|[+-]\d{2}:\d{2}$/.test(raw) ? m : `${m}Z`
    );
    const d = new Date(normalised);
    if (isNaN(d)) return "NA";
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " UTC";
  };

  const formatMinutes = (mins) => {
    if (mins === undefined || mins === null || mins === "") return "—";
    const n = parseFloat(mins);
    if (isNaN(n)) return "—";
    if (n < 60) return `${Math.round(n)} min`;
    const h = Math.floor(n / 60);
    const m = Math.round(n % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const mttdColor = (mins) => {
    const n = parseFloat(mins);
    if (isNaN(n)) return "neutral";
    return n <= 15 ? "success" : "error";
  };

  const mttrColor = (mins) => {
    const n = parseFloat(mins);
    if (isNaN(n)) return "neutral";
    return n <= 30 ? "success" : "error";
  };

  const KpiCard = ({ label, value, color }) => {
    const textColor =
      color === "error" ? "var(--mds_brand_appearance_error_default_text-color, #B80012)" :
        color === "success" ? "var(--mds_brand_appearance_success_default_text-color, #2C8A4D)" :
          undefined;
    return (
      <div className="report-meta-item">
        <span className="report-meta-label">{label}</span>
        <span className="report-kpi-value" style={textColor ? { color: textColor } : undefined}>
          {value}
        </span>
      </div>
    );
  };

  const snowLink = process.env.REACT_APP_SNOW_LINK;

  const status = postmortemData.postMortemIncomplete
    ? "INCOMPLETE"
    : postmortemData.status;

  const StatusBadge = ({ status: s, ageing }) => {
    const isDone = s?.toUpperCase() === "DONE";
    const isOld = ageing != null && ageing >= 5;
    const RED = "var(--mds_brand_appearance_error_default_text-color, #B80012)";
    const GREEN = "var(--mds_brand_appearance_success_default_text-color, #2C8A4D)";
    const color = isOld ? RED : GREEN;
    const shouldPulse = !isDone && isOld;
    const elapsed = ageing != null ? `Leadership Review Age: ${ageing}d` : "";
    const daysOver = ageing != null ? ageing - 5 : 0;

    // Compute Xd Yh for DONE from actual timestamps
    const closedIn = (() => {
      const t1 = incidentMetadata?.postmortemCreatedTime;
      const t2 = incidentMetadata?.postmortemCompletedTime;
      if (t1 && t2) {
        const diffMs = new Date(t2) - new Date(t1);
        const totalMins = Math.floor(diffMs / 60000);
        const d = Math.floor(totalMins / (60 * 24));
        const h = Math.floor((totalMins % (60 * 24)) / 60);
        return d > 0 ? `${d}d ${h}h` : `${h}h`;
      }
      return ageing != null ? `${ageing}d` : null;
    })();

    let cta, sub;
    switch (s?.toUpperCase()) {
      case "DRAFT":
        cta = "Pending Leadership Review"; sub = `RCA in progress · ${elapsed}`; break;
      case "LEADERSHIP_REVIEW":
        cta = "In Leadership Review"; sub = `Awaiting ED or TLT approval · ${elapsed}`; break;
      case "DONE":
        cta = "Postmortem Closed";
        sub = closedIn
          ? (isOld ? `Closed in ${closedIn} · Leadership Review Age: ${ageing}d · SLA exceeded by ${daysOver}d` : `Closed in ${closedIn} · Leadership Review Age: ${ageing}d · Within SLA`)
          : (isOld ? `Leadership Review Age: ${ageing}d · SLA exceeded by ${daysOver}d` : `Leadership Review Age: ${ageing}d · Within SLA`);
        break;
      default:
        cta = s || "—"; sub = elapsed;
    }

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
          <span className={`report-status-dot${shouldPulse ? " report-status-pulse" : ""}`} style={{ background: color }} />
          <span style={{ fontSize: "13px", fontWeight: 700, color }}>{cta}</span>
        </div>
        <span style={{ fontSize: "11px", color: "#141414", lineHeight: "16px" }}>{sub}</span>
      </div>
    );
  };

  const MetaItem = ({ label, children }) => (
    <div className="report-meta-item">
      <span className="report-meta-label">{label}</span>
      <span className="report-meta-value">{children || "NA"}</span>
    </div>
  );

  const reportTitle = postmortemData.short_description || postmortemData.application || postmortemData.product;
  const priorityLabel = getPriorityLabel(postmortemData.priority);

  return (
    <div className="report-container">
      <div className="sre-section-card">
        <div className="sre-section-header" style={{ background: "#42B0D5" }}>
          <span className="sre-section-header-title" style={{ fontSize: "14px", letterSpacing: "0.04em", paddingLeft: "8px" }}>
            {reportTitle}
          </span>
          <div className="report-title-badges">
            {fetchRcaQuality && postmortemData?.incidentID && (
              <RcaQualityPanel
                incidentID={postmortemData.incidentID}
                postMortemId={postMortemId}
                fetchQuality={fetchRcaQuality}
                fetchEnhancements={fetchEnhancements}
                postmortemData={postmortemData}
                recurrenceContent={
                  userIsAdmin ? (
                    <RecurrenceAnalysisPanel
                      incidentId={incidentID}
                      onRecurrenceChanged={onRecurrenceChanged}
                      canAnalyze={!!(canUserEditReport && canUserEditReport())}
                    />
                  ) : undefined
                }
              />
            )}
            {onExportPdf && (
              <McButton
                appearance="inverse"
                variant="outlined"
                fit="small"
                icon="file-pdf"
                label={pdfDownloading ? "Generating…" : "Export PDF"}
                disabled={pdfDownloading}
                onClick={onExportPdf}
              />
            )}
            {onEdit && (
              <McButton
                appearance="inverse"
                variant="outlined"
                fit="small"
                icon="pencil"
                label="Edit"
                disabled={!canUserEditReport || !canUserEditReport()}
                title={!canUserEditReport || !canUserEditReport() ? "Only SRE and LT team members can edit postmortems" : ""}
                onClick={onEdit}
              />
            )}
            {onShare && (
              <McButton
                appearance="inverse"
                variant="filled"
                fit="small"
                icon="share"
                label="Share"
                onClick={onShare}
              />
            )}
          </div>
        </div>

        <div className="sre-section-body report-header-body">
          {/* Row 1 — Incident Metadata + KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", borderBottom: "1px solid #E2E2E2" }}>
            <div className="report-meta-item" style={{ borderRight: "1px solid #E2E2E2", borderBottom: "none" }}>
              <span className="report-meta-label">Incident</span>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                {postmortemData.incidentID ? (
                  snowLink ? (
                    <a href={`${snowLink}number=${postmortemData.incidentID}`} target="_blank" rel="noopener noreferrer"
                      className="report-meta-value" style={{ color: "var(--mds_brand_appearance_primary_default_text-color, #0073ab)", textDecoration: "underline", textUnderlineOffset: "2px" }}>
                      {priorityLabel ? `${priorityLabel} | ${postmortemData.incidentID}` : postmortemData.incidentID}
                    </a>
                  ) : <span className="report-meta-value">{priorityLabel ? `${priorityLabel} | ${postmortemData.incidentID}` : postmortemData.incidentID}</span>
                ) : <span className="report-meta-value">{priorityLabel ? `${priorityLabel} | NA` : "NA"}</span>}
                {incidentMetadata?.ircLink ? (
                  <a href={incidentMetadata.ircLink} target="_blank" rel="noopener noreferrer"
                    className="report-meta-value" style={{ color: "var(--mds_brand_appearance_primary_default_text-color, #0073ab)", textDecoration: "underline" }}>
                    Open Hedwig IRC ↗
                  </a>
                ) : null}
              </div>
            </div>
            <KpiCard label="Mean Time to Detect" value={formatMinutes(mttd)} color={mttdColor(mttd)} />
            <KpiCard label="Mean Time to Recover" value={formatMinutes(mttr)} color={mttrColor(mttr)} />
            <KpiCard label="Captured by Alert" value={capturedByAlert ? "Yes" : "No"} color={capturedByAlert ? "success" : "error"} />
          </div>

          {/* Row 2 — Dates + Affected + Caused By */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", borderBottom: "1px solid #E2E2E2" }}>
            {/* Created On */}
            <div className="report-meta-item" style={{ borderRight: "1px solid #E2E2E2", borderBottom: "none" }}>
              <span className="report-meta-label">Created On</span>
              <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>
                {formatUtcDate(incidentMetadata?.incidentCreatedOn)}
              </span>
            </div>
            {/* Affected Locations & Client */}
            <div className="report-meta-item" style={{ borderRight: "1px solid #E2E2E2", borderBottom: "none" }}>
              <span className="report-meta-label">Affected Locations</span>
              {incidentMetadata?.affectedLocations && incidentMetadata.affectedLocations !== "NA" ? (
                <div style={{ 
                  marginTop: "4px", 
                  maxHeight: "80px", 
                  overflowY: "auto", 
                  display: "flex", 
                  flexWrap: "wrap", 
                  gap: "4px",
                  padding: "2px 0"
                }}>
                  {incidentMetadata.affectedLocations.split(",").map((loc, idx) => (
                    <span key={idx} style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      backgroundColor: "#F0F4F8",
                      borderRadius: "12px",
                      fontSize: "11px",
                      color: "#333",
                      whiteSpace: "nowrap"
                    }}>
                      {loc.trim()}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>NA</span>
              )}
              {incidentMetadata?.affectedClient && incidentMetadata.affectedClient !== "NA" && (
                <>
                  <span className="report-meta-label" style={{ display: "block", marginTop: "8px" }}>Affected Customer</span>
                  <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>
                    {incidentMetadata.affectedClient}
                  </span>
                </>
              )}
            </div>
            {/* Affected Application */}
            <div className="report-meta-item" style={{ borderRight: "1px solid #E2E2E2", borderBottom: "none" }}>
              <span className="report-meta-label">Affected Application</span>
              <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>{postmortemData.cmdb_ci || "NA"}</span>
            </div>
            {/* Caused By Application */}
            <div className="report-meta-item" style={{ borderBottom: "none" }}>
              <span className="report-meta-label">Caused By Application</span>
              <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>{postmortemData.application || "NA"}</span>
            </div>
          </div>

          {/* Row 3 — Postmortem Metadata | Status & SLA | Root Cause & Recurrence */}
          {(() => {
            const isVendor = postmortemData?.rootCause?.causedByVendor && postmortemData.rootCause.causedByVendor !== "No" && postmortemData.rootCause.causedByVendor !== "";
            const vendorName = postmortemData?.rootCause?.vendorOptions || postmortemData?.rootCause?.vendorDetails;
            const isChange = postmortemData?.rootCause?.impactedByChange === true || postmortemData?.rootCause?.impactedByChange === "true" || postmortemData?.rootCause?.impactedByChange === "Yes";
            const changeNumber = postmortemData?.rootCause?.causedByChangeNumber;
            const isRecurringIssue = (val) => val && val !== "No" && val !== "false" && val !== false && val !== "no";
            const isRecurring = !!(isRecurringIssue(postmortemData?.recurrence?.recurringIssue) || isRecurringIssue(postmortemData?.rootCause?.recurringIssue));
            const recurringIncidentId = joinReferenceIds(getPastReferencesList(postmortemData?.recurrence));
            const errStyle = { color: "var(--mds_brand_appearance_error_default_text-color,#B80012)", fontWeight: 700 };
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
                {/* Postmortem Owner */}
                <div className="report-meta-item" style={{ borderRight: "1px solid #E2E2E2", borderBottom: "none" }}>
                  <span className="report-meta-label">Postmortem Owner</span>
                  <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>
                    {formatAssignedToDisplay(postmortemData.assigned_to)}
                  </span>
                </div>
                {/* Postmortem Status */}
                <div className="report-meta-item" style={{ borderRight: "1px solid #E2E2E2", borderBottom: "none" }}>
                  <span className="report-meta-label" style={{ display: "block", marginBottom: "8px" }}>Postmortem Status</span>
                  <StatusBadge status={status} ageing={incidentMetadata?.ageing} />
                </div>
                {/* Root Cause Code — always; + Sub Code when flags are present */}
                <div className="report-meta-item" style={{ borderRight: "1px solid #E2E2E2", borderBottom: "none" }}>
                  <div>
                    <span className="report-meta-label">Root Cause Code</span>
                    <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>{postmortemData?.rootCause?.rootCauseCode || "NA"}</span>
                  </div>
                  {(isChange || isVendor || isRecurring) && (
                    <div style={{ marginTop: "12px" }}>
                      <span className="report-meta-label">Root Cause Sub Code</span>
                      <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>{postmortemData?.rootCause?.rootCauseSubCode || "NA"}</span>
                    </div>
                  )}
                </div>
                {/* Cell 4: flags when present, else Sub Code */}
                <div className="report-meta-item" style={{ borderBottom: "none" }}>
                  {(isChange || isVendor || isRecurring) ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {isChange && (
                        <div>
                          <span className="report-meta-label">Caused by Change</span>
                          <span className="report-meta-value" style={{ display: "block", marginTop: "4px", ...errStyle }}>
                            Yes{changeNumber ? ` — ${changeNumber}` : ""}
                          </span>
                        </div>
                      )}
                      {isVendor && (
                        <div>
                          <span className="report-meta-label">Caused By Vendor</span>
                          <span className="report-meta-value" style={{ display: "block", marginTop: "4px", ...errStyle }}>
                            Yes{vendorName ? ` — ${vendorName}` : ""}
                          </span>
                        </div>
                      )}
                      {isRecurring && (
                        <div>
                          <span className="report-meta-label">Recurrence</span>
                          <span className="report-meta-value" style={{ display: "block", marginTop: "4px", ...errStyle }}>
                            Yes{recurringIncidentId ? ` — ${recurringIncidentId}` : ""}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <span className="report-meta-label">Root Cause Sub Code</span>
                      <span className="report-meta-value" style={{ display: "block", marginTop: "4px" }}>{postmortemData?.rootCause?.rootCauseSubCode || "NA"}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Approval Workflow Section (Positioned after Metadata) ─────────────────────────────── */}
      {approvalStatus && isUserAuthorizedToViewApproval && isUserAuthorizedToViewApproval() && (
        <div style={{ marginTop: "24px", marginBottom: "24px" }}>
          <ApprovalWorkflowTracker
            currentStage={approvalStatus.currentStage || "DRAFT"}
            status={approvalStatus.status || "NOT_SUBMITTED"}
            leadershipReviewCompleted={approvalStatus.leadershipReviewCompleted}
          />

          {approvalStatus.currentStage === "LEADERSHIP_REVIEW" && (
            <ApprovalPanel
              approvers={approvalStatus.leadershipReviewApprovers || []}
              currentStage={approvalStatus.currentStage}
              status={approvalStatus.status}
              onApprove={handleApproveLeadershipReview}
              onReject={handleRejectLeadershipReview}
              canApprove={canUserApprove()}
              loading={approvalLoading}
              routingInfo={`Routed to (Portfolio): ${postmortemData?.portfolio || "portfolio"} leadership (ED + TLT).`}
              leadershipReviewCompleted={approvalStatus.leadershipReviewCompleted}
              leadershipReviewApprovedBy={approvalStatus.leadershipReviewApprovedBy}
              leadershipReviewApprovedAt={approvalStatus.leadershipReviewApprovedAt}
              leadershipReviewApproverType={approvalStatus.leadershipReviewApproverType}
              leadershipReviewComment={approvalStatus.leadershipReviewComment}
            />
          )}
        </div>
      )}

      {/* ── Sections ─────────────────────────────── */}
      <div
        id="pdf-section"
        style={{ wordBreak: "break-word" }}
      >

        {/* Incident Summary */}
        {(() => {
          const summaryInsight = findInsight(["summary", "incident"]);
          const hasSummaryInsight = !!summaryInsight && (
            summaryInsight.recommendations?.length > 0 ||
            summaryInsight.missing?.length > 0 ||
            !!summaryInsight.whyLow
          );
          const showSummaryPanel = summaryAiOpen && hasSummaryInsight;
          return (
            <div id="summary" className="sre-section-card">
              <div className="sre-section-header" style={{ background: "#42B0D5" }}>
                <span className="sre-section-header-title">Incident Summary</span>
                <McButton
                  appearance="inverse"
                  variant="outlined"
                  fit="small"
                  icon="sparkle"
                  label="AI Review"
                  onClick={() => setSummaryAiOpen((v) => !v)}
                />
              </div>
              <div className={`sre-section-body${showSummaryPanel ? " sre-section-body--with-ai" : ""}`}>
                <div className={showSummaryPanel ? "sre-section-content" : undefined}>
                  <SectionContent
                    sectionData={postmortemData?.incident_summary?.summary}
                    images={uploadedSummaryImages}
                  />
                </div>
                {showSummaryPanel && (
                  <div className="sre-section-ai-col">
                    <AiSuggestionsInline insight={summaryInsight} sectionName="Incident Summary" dim={findDim(["incident summary", "summary"])} onClose={() => setSummaryAiOpen(false)} />
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Preceding Events — only for old postmortems that have this data */}
        {postmortemData?.precedingEvents?.description?.replace(/<[^>]*>/g, "").trim() && (
          <SectionCard id="preceding-events" title="Preceding Events">
            <SectionContent
              sectionData={postmortemData.precedingEvents.description}
              images={uploadedPrecedingEventsImages}
            />
          </SectionCard>
        )}

        {/* Business Impact — only for old postmortems that have this data */}
        {postmortemData?.businessImpact?.description?.replace(/<[^>]*>/g, "").trim() && (
          <SectionCard id="business-impact" title="Business Impact">
            <SectionContent
              sectionData={postmortemData.businessImpact.description}
              images={uploadedImpactImages}
            />
          </SectionCard>
        )}

        {/* Recovery — only for old postmortems that have this data */}
        {postmortemData?.recovery?.recovery?.replace(/<[^>]*>/g, "").trim() && (
          <SectionCard id="recovery" title="Recovery">
            <SectionContent sectionData={postmortemData.recovery.recovery} />
          </SectionCard>
        )}

        {/* Root Cause */}
        <SectionCard id="root-cause" title="Root Cause Identification" insight={findInsight(["root", "cause"])} dim={findDim(["root cause"])}>
          <RootCauseIdentificationSection
            data={postmortemData?.rootCauseIdentificationRequest}
            images={rootCauseIdentificationUploadedImages}
            incidentID={incidentID}
          />
          {postmortemData?.rootCause?.cause?.replace(/<[^>]*>/g, "").trim() && (
            <>
              <p className="report-subsection-header">Root Cause</p>
              <div style={{ padding: "12px 16px" }}>
                <SectionContent sectionData={postmortemData.rootCause.cause} />
              </div>
            </>
          )}
          <p className="report-subsection-header">Root Cause Code</p>
          <RootCausection
            data={postmortemData?.rootCause}
            recurrenceData={postmortemData?.recurrence}
            images={uploadedRootCauseImages}
          />
        </SectionCard>

        {/* Preventive & Corrective Measures */}
        <SectionCard
          id="preventitive-and-corrective-measures"
          title="Preventive & Corrective Measures"
          insight={findInsight(["preventive", "corrective", "action", "measure"])}
          dim={findDim(["preventive", "corrective"])}
        >
          {onSavePreventiveMeasures && canUserEditReport && canUserEditReport() ? (
            <ReportTrackingMethodManager
              key={postMortemId}
              postmortemData={postmortemData}
              incidentID={incidentID}
              onSave={onSavePreventiveMeasures}
            />
          ) : (
            <>
              <JiraSection
                data={
                  postmortemData?.preventiveCorrectiveMeasures &&
                  postmortemData?.preventiveCorrectiveMeasures[0]?.jira
                }
                images={jiraUploadedImages}
              />
              <PTaskSection
                data={
                  postmortemData?.preventiveCorrectiveMeasures &&
                  postmortemData?.preventiveCorrectiveMeasures[0]?.ptask
                }
                images={ptaskUploadedImages}
              />
            </>
          )}
        </SectionCard>

        {/* MVC Vulnerability — APMT portfolio only */}
        {postmortemData.portfolio === "APMT, IOT and Automation" && (
          <SectionCard title="MVC Vulnerability Related">
            <div className="report-content-box">
              {postmortemData.mvcVulnerabilityRelated &&
                postmortemData.mvcVulnerabilityRelated.length > 0
                ? postmortemData.mvcVulnerabilityRelated.join(", ")
                : "NA"}
            </div>
          </SectionCard>
        )}

        {/* ── Approval History (Audit Trail at Bottom) ─────────────────────────────── */}
        {approvalHistory && approvalHistory.length > 0 && isUserAuthorizedToViewApproval && isUserAuthorizedToViewApproval() && (
          <div style={{ marginTop: "32px", marginBottom: "24px" }}>
            <ApprovalHistory history={approvalHistory} />
          </div>
        )}

      </div>

      <RecommendedActionsModal
        data={postmortemData?.recommendedActions}
        modalOpen={recommendationModal}
        handleModal={handleRecommedationModalClose}
      />
    </div>
  );
}

export default Report;
